import { create } from 'zustand';
import { createClient } from '@supabase/supabase-js';
import logger from '@/logger';
import {
	FincluCustomer,
	CustomerProcessStatus,
	FincluCustomerVehicleOffering,
	FincluCustomerQuestionnaire,
	SocialNetworkProfileRecord,
	DocumentRecord
} from '@/pages/CustomerSupport/types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseKey);

const PAGE_SIZE = 10;

export interface CustomerFilters {
	status?: CustomerProcessStatus[];
	state?: string;
	searchTerm?: string;
	dateFrom?: string;
	dateTo?: string;
	fitForVehicularCredit?: boolean | null;
	fincluStatuses?: string[];
}

export interface CustomerSorting {
	column: string;
	direction: 'asc' | 'desc';
}

export interface CustomerAnalysisMetrics {
	customer_information?: any;
	ai_interview_analysis?: any;
	default_score_analysis?: any;
	social_network_analysis?: any;
	mobility_platform_analysis?: any;
}

interface CustomerStore {
	customers: FincluCustomer[];
	selectedCustomer: FincluCustomer | null;
	vehicleOffering: FincluCustomerVehicleOffering | null;
	questionnaire: FincluCustomerQuestionnaire | null;
	socialNetworkProfiles: SocialNetworkProfileRecord[];
	documentRecord: DocumentRecord | null;
	analysisMetrics: CustomerAnalysisMetrics | null;
	isLoading: boolean;
	error: string | null;
	totalCount: number;
	hasMore: boolean;
	filters: CustomerFilters;
	sorting: CustomerSorting;

	loadCustomers: (organizationId?: string) => Promise<void>;
	loadMoreCustomers: (organizationId?: string) => Promise<void>;
	loadCustomer: (customerId: string) => Promise<FincluCustomer | null>;
	updateCustomer: (customerId: string, updates: Partial<FincluCustomer>) => Promise<boolean>;
	setFilters: (filters: CustomerFilters) => void;
	setSorting: (sorting: CustomerSorting) => void;
	setSelectedCustomer: (customer: FincluCustomer | null) => void;
	resetError: () => void;
}

const buildCustomerQuery = (
	filters: CustomerFilters,
	sorting: CustomerSorting,
	organizationId?: string
) => {
	let query = supabase.from('customer_information').select('*', { count: 'exact' });

	if (organizationId) {
		query = query.eq('whatsapp_organization_id', organizationId);
	}

	if (filters.status && filters.status.length > 0) {
		query = query.in('customer_process_status', filters.status);
	}

	if (filters.state) {
		query = query.eq('state', filters.state);
	}

	if (filters.searchTerm && filters.searchTerm.trim()) {
		const term = filters.searchTerm.trim();
		query = query.or(
			`names.ilike.%${term}%,first_last_name.ilike.%${term}%,second_last_name.ilike.%${term}%,phone_number.ilike.%${term}%,email.ilike.%${term}%`
		);
	}

	if (filters.dateFrom) {
		query = query.gte('created_at', filters.dateFrom);
	}

	if (filters.dateTo) {
		query = query.lte('created_at', filters.dateTo);
	}

	if (filters.fitForVehicularCredit !== undefined && filters.fitForVehicularCredit !== null) {
		query = query.eq('fit_for_vehicular_credit', filters.fitForVehicularCredit);
	}

	if (filters.fincluStatuses && filters.fincluStatuses.length > 0) {
		query = query.in('finclu_operation_status', filters.fincluStatuses);
	}

	query = query.order(sorting.column, { ascending: sorting.direction === 'asc' });

	return query;
};

export const useCustomerStore = create<CustomerStore>((set, get) => ({
	customers: [],
	selectedCustomer: null,
	vehicleOffering: null,
	questionnaire: null,
	socialNetworkProfiles: [],
	documentRecord: null,
	analysisMetrics: null,
	isLoading: false,
	error: null,
	totalCount: 0,
	hasMore: false,
	filters: {
		status: [
			CustomerProcessStatus.SENT_APPROVED_MESSAGE,
			CustomerProcessStatus.AGENT_FILLING_QUESTIONNARE,
			CustomerProcessStatus.AGENT_SUGGESTING_VEHICLE,
			CustomerProcessStatus.AGENT_GATHERING_DOCUMENTS,
			CustomerProcessStatus.AGENT_GATHERING_SOCIAL_NETWORKS,
			CustomerProcessStatus.AGENT_ANALYZING_SOCIAL_NETWORKS,
			CustomerProcessStatus.AGENT_RISK_CALCULATION
		]
	},
	sorting: {
		column: 'created_at',
		direction: 'asc' as const
	},

	loadCustomers: async (organizationId?: string) => {
		try {
			set({ isLoading: true, error: null });

			const { filters, sorting } = get();

			const query = buildCustomerQuery(filters, sorting, organizationId).range(0, PAGE_SIZE - 1);

			const { data, error, count } = await query;

			if (error) {
				logger.error('Error loading customers:', error);
				set({ error: error.message, isLoading: false });
				return;
			}

			const totalCount = count || 0;
			const loadedCount = (data || []).length;

			set({
				customers: data || [],
				totalCount,
				hasMore: loadedCount < totalCount,
				isLoading: false
			});
		} catch (err) {
			logger.error('Error loading customers:', err);
			set({
				error: 'Failed to load customers',
				isLoading: false
			});
		}
	},

	loadMoreCustomers: async (organizationId?: string) => {
		const { customers, isLoading, hasMore, filters, sorting } = get();

		if (isLoading || !hasMore) {
			return;
		}

		try {
			set({ isLoading: true, error: null });

			const from = customers.length;
			const to = from + PAGE_SIZE - 1;

			const query = buildCustomerQuery(filters, sorting, organizationId).range(from, to);

			const { data, error, count } = await query;

			if (error) {
				logger.error('Error loading more customers:', error);
				set({ error: error.message, isLoading: false });
				return;
			}

			const newCustomers = [...customers, ...(data || [])];
			const totalCount = count || 0;

			set({
				customers: newCustomers,
				totalCount,
				hasMore: newCustomers.length < totalCount,
				isLoading: false
			});
		} catch (err) {
			logger.error('Error loading more customers:', err);
			set({
				error: 'Failed to load more customers',
				isLoading: false
			});
		}
	},

	loadCustomer: async (customerId: string) => {
		try {
			set({ isLoading: true, error: null });

			const { data, error } = await supabase
				.from('customer_information')
				.select('*')
				.eq('id', customerId)
				.single();

			if (error) {
				logger.error('Error loading customer:', error);
				set({ error: error.message });
				return null;
			}

			const { data: vehicleOfferingData } = await supabase
				.from('finclu_customer_vehicle_offering')
				.select('*')
				.eq('customer_id', customerId)
				.order('created_at', { ascending: false })
				.limit(1)
				.maybeSingle();

			const { data: questionnaireData } = await supabase
				.from('finclu_customer_questionnaire')
				.select('*')
				.eq('customer_id', customerId)
				.order('updated_at', { ascending: false })
				.limit(1)
				.maybeSingle();

			const { data: socialNetworkProfilesData } = await supabase
				.from('finclu_customer_social_network_profiles')
				.select('*')
				.eq('customer_id', customerId)
				.order('created_at', { ascending: false });

			const { data: documentRecordData } = await supabase
				.from('finclu_customer_documents')
				.select('*')
				.eq('customer_id', customerId)
				.order('created_at', { ascending: false })
				.limit(1)
				.maybeSingle();

			const { data: metricsData, error: metricsError } = await supabase
				.from('customer_analysis_metrics')
				.select('*')
				.eq('customer_id', customerId)
				.order('created_at', { ascending: false })
				.limit(1)
				.maybeSingle();

			if (metricsError) {
				logger.error('Error loading metrics:', metricsError);
			}

			const parsedMetrics = metricsData?.metrics || metricsData;

			set({
				selectedCustomer: data,
				vehicleOffering: vehicleOfferingData || null,
				questionnaire: questionnaireData || null,
				socialNetworkProfiles: socialNetworkProfilesData || [],
				documentRecord: documentRecordData || null,
				analysisMetrics: parsedMetrics || null,
				isLoading: false
			});
			return data;
		} catch (err) {
			logger.error('Error loading customer:', err);
			set({ error: 'Failed to load customer' });
			return null;
		}
	},

	updateCustomer: async (customerId: string, updates: Partial<FincluCustomer>) => {
		try {
			set({ error: null });

			const { error: updateError } = await supabase
				.from('customer_information')
				.update({
					...updates,
					updated_at: new Date().toISOString(),
					version: get().selectedCustomer?.version ? get().selectedCustomer!.version + 1 : 1
				})
				.eq('id', customerId);

			if (updateError) {
				logger.error('Error updating customer:', updateError);
				set({ error: updateError.message });
				return false;
			}

			set((state) => ({
				customers: state.customers.map((c) => (c.id === customerId ? { ...c, ...updates } : c)),
				selectedCustomer:
					state.selectedCustomer?.id === customerId
						? { ...state.selectedCustomer, ...updates }
						: state.selectedCustomer
			}));

			return true;
		} catch (err) {
			logger.error('Error updating customer:', err);
			set({ error: 'Failed to update customer' });
			return false;
		}
	},

	setFilters: (filters: CustomerFilters) => {
		set({ filters });
	},

	setSorting: (sorting: CustomerSorting) => {
		set({ sorting });
	},

	setSelectedCustomer: (customer) => {
		set({ selectedCustomer: customer });
	},

	resetError: () => set({ error: null })
}));
